'use client';
/**
 * Верхняя панель: где я нахожусь (крошки) + быстрый переход (Cmd+K) +
 * организация, тема, уведомления, профиль.
 *
 * Раньше поиск висел плавающим островом поверх контента, а понять, в
 * каком разделе находишься, можно было только по подсветке в сайдбаре.
 * Теперь путь всегда написан словами.
 */
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { ChevronRight, Sun, Moon } from 'lucide-react';
import { useT } from '@/lib/useT';
import { useTheme } from '@/lib/theme';
import { useAuthStore } from '@/store/auth.store';
import { IconButton } from '@/components/ui';
import { OrgSwitcher } from '@/components/OrgSwitcher';
import NotificationBell from '@/components/layout/NotificationBell';
import CommandPalette from './CommandPalette';
import UserMenu from './UserMenu';
import { areaForPath, leafForPath, type Role } from '@/lib/navigation';

export default function TopBar({ role }: { role: Role }) {
  const pathname = usePathname() || '';
  const t = useT();
  const { theme, toggle } = useTheme();
  const { user } = useAuthStore();

  const label = (l?: { labelKey: string; fallback: string } | null) => {
    if (!l) return null;
    const v = t(l.labelKey);
    return !v || v === l.labelKey ? l.fallback : v;
  };

  const area = role === 'admin' ? areaForPath(pathname) : null;
  const leaf = leafForPath(pathname, role);
  const areaName = label(area);
  const leafName = label(leaf);

  return (
    <header
      className="sticky top-0 z-30 flex items-center gap-3 h-[52px] px-4 bg-[var(--surface)] border-b border-[var(--border)]"
    >
      {/* Крошки: область → экран */}
      <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 min-w-0">
        {areaName && areaName !== leafName && (
          <>
            <span className="text-[12.5px] text-[var(--text-muted)] truncate">{areaName}</span>
            <ChevronRight size={12} className="text-[var(--text-muted)] shrink-0" />
          </>
        )}
        <span className="text-[13px] font-semibold text-[var(--text-primary)] truncate">
          {leafName ?? areaName ?? 'Dashboard'}
        </span>
      </nav>

      <div className="flex-1 flex justify-center px-4 min-w-0">
        <CommandPalette role={role} />
      </div>

      <div className="flex items-center gap-1.5 shrink-0">
        <OrgSwitcher />
        <NotificationBell />
        <IconButton
          icon={theme === 'dark' ? Sun : Moon}
          label={theme === 'dark' ? 'Light theme' : 'Dark theme'}
          onClick={toggle}
        />
        <UserMenu />
      </div>
    </header>
  );
}
